import type { Component } from 'solid-js'
import { createSignal } from 'solid-js'
import type { SubscriberFilters } from '../../features/subscribers/domain/models/subscriber'
import { Icon } from '@iconify-icon/solid'
import { useDebounce } from '../../shared/helpers/debounce'
import RegionFilter from '../shared/components/RegionFilter'
import UserDirectorySubscriptionFilter from './UserDirectorySubscriptionFilter'
import ExportCsvButton from './ExportCsvButton'
import { useTransContext } from '@mbarzda/solid-i18next'

interface UserDirectoryFiltersProps {
  searchTerm: string
  fuzzy: boolean
  filters: SubscriberFilters
  selectedCountries: string[]
  selectedRegions: string[]
  selectedPlans: string[]
  selectedLevels: string[]
  selectedPreviousLevels: string[]
  selectedStatuses: string[]
  onSearch: (value: string) => void
  onFuzzyChange: (value: boolean) => void
  onFilterChange: (type: 'country' | 'region' | 'plan' | 'level' | 'previousLevel' | 'status', values: string[]) => void
  buildExportParams: () => URLSearchParams | null
}

const UserDirectoryFilters: Component<UserDirectoryFiltersProps> = (props) => {
  const [t] = useTransContext()
  const [searchInput, setSearchInput] = createSignal(props.searchTerm)

  const debouncedSearch = useDebounce((value: string) => props.onSearch(value), 350)

  const handleSearchInput = (e: Event) => {
    const target = e.target as HTMLInputElement
    setSearchInput(target.value)
    debouncedSearch(target.value)
  }

  return (
    <div class="flex items-start gap-8">
      {/* Search */}
      <div class="min-w-0 flex-1">
        <div class="flex items-center gap-2.5 rounded-md border border-gray-200 bg-white px-3 py-2">
          <div class="text-gray-400">
            <Icon icon="heroicons:magnifying-glass" class="text-[1rem]" />
          </div>
          <input
            type="text"
            value={searchInput()}
            onInput={handleSearchInput}
            placeholder={t('ls_user_directory_search_placeholder')}
            class="flex-1 border-0 bg-transparent p-0 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-0"
          />
          <button
            type="button"
            aria-pressed={props.fuzzy}
            title={t('ls_user_directory_fuzzy_search_hint')}
            onClick={() => props.onFuzzyChange(!props.fuzzy)}
            class="-my-1 flex flex-shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-[background-color,border-color,color,transform] duration-150 ease-out active:scale-[0.97]"
            classList={{
              'border-primary-200 bg-primary-50 text-primary-700': props.fuzzy,
              'border-gray-200 bg-white text-gray-600 hover:bg-gray-50': !props.fuzzy
            }}
          >
            <Icon icon={props.fuzzy ? 'heroicons:check-circle-solid' : 'heroicons:sparkles'} class="text-[0.875rem]" />
            {t('ls_user_directory_fuzzy_search')}
          </button>
        </div>
      </div>

      {/* Filter dropdowns */}
      <div class="flex flex-shrink-0 items-center gap-4">
        <RegionFilter
          data={props.filters.regions}
          selectedCountries={props.selectedCountries}
          selectedRegions={props.selectedRegions}
          onCountryChange={(countries) => props.onFilterChange('country', countries)}
          onRegionChange={(regions) => props.onFilterChange('region', regions)}
        />

        <UserDirectorySubscriptionFilter
          subscriptionPlans={props.filters.subscription_plans ?? []}
          subscriptionLevels={props.filters.subscription_levels}
          previousSubscriptionLevels={props.filters.previous_subscription_levels}
          subscriptionStatuses={props.filters.subscription_statuses}
          selectedPlans={props.selectedPlans}
          selectedLevels={props.selectedLevels}
          selectedPreviousLevels={props.selectedPreviousLevels}
          selectedStatuses={props.selectedStatuses}
          onToggle={(type, values) => props.onFilterChange(type, values)}
        />

        <ExportCsvButton buildParams={props.buildExportParams} />
      </div>
    </div>
  )
}

export default UserDirectoryFilters
